import { useCallback, useEffect, useRef, useState } from "react";

export interface UseAsyncResult<T> {
  data: T | undefined;
  loading: boolean;
  error: Error | undefined;
  refetch: () => Promise<void>;
}

/**
 * Lehký fetcher hook bez cache. Každé volání = fresh request (žádný TanStack Query).
 * Vhodný pro IoT dashboard, kde čerstvost > performance.
 *
 * Pattern "fetch on mount + setState" je cíl tohoto hooku — `setState` v efektu je
 * záměrné (subscribe na external system = HTTP). Pravidla `react-hooks/refs` a
 * `react-hooks/set-state-in-effect` zde záměrně potlačujeme s odůvodněním.
 *
 * Používání:
 *   const { data, loading, error, refetch } = useAsync(() => getNotificationRules(), [userId]);
 */
export const useAsync = <T>(fn: () => Promise<T>, deps: unknown[] = []): UseAsyncResult<T> => {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const aliveRef = useRef(true);
  const fnRef = useRef(fn);

  // Synchronizujeme `fn` ref v efektu (ne během renderu, aby pravidla pro refy byla čistá)
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const run = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await fnRef.current();
      if (aliveRef.current) setData(result);
    } catch (e) {
      if (aliveRef.current) {
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    // setState volání v `run()` je záměrné - synchronizace s external HTTP systemem.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    run();
    return () => {
      aliveRef.current = false;
    };
    // deps je dynamický array kontrolovaný callerem; exhaustive-deps zde nemůže pomoct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch: run };
};
